import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate, useParams} from "react-router";
import {Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
        IconButton, Radio, Checkbox, Switch, TextField, ToggleButton, ToggleButtonGroup,
        Tooltip, Typography} from "@mui/material";
import {ArrowBack, ArrowForward, ArrowBackOutlined, ContentCopyOutlined, DeleteOutlineOutlined,
        AutoAwesomeOutlined, LightbulbOutlined, SettingsOutlined} from "@mui/icons-material";

import {getQuiz, Proposal, Question, QuestionType, proposeQuestions, saveQuiz} from "../data/quiz";
import {useSessionContext} from "../util/SessionContext";
import {Blue3, Grey2, Grey5, Grey6, Grey17, White} from "../theme/styles";

// The Quiz Builder, at "/quiz/:usid" where the USID is the ObjectRevision's.
//
// A PAGE RATHER THAN A DIALOG, because authoring ten questions with four choices and four rationales
// each is work, not a setting -- and because "Back To Document" is a truthful thing to offer only if
// there is somewhere to go back FROM.
//
// THE WHOLE QUIZ IS HELD HERE AND SAVED AS A WHOLE, matching the server, which replaces the questions
// wholesale. Saving per keystroke would write half-built questions that fail validation, and saving per
// question would leave the rail and the database disagreeing about the order.
//
// ASSIST WITH AI PROPOSES, IT DOES NOT WRITE. What comes back is reviewed one question at a time and
// accepted or discarded; only accepting puts it in the builder, and only Save Quiz puts it in the
// database. In a regulated context that is the whole distinction -- the machine suggested it, a person
// took responsibility for it -- and it is enforced by the service having no write path, not by this page
// choosing to be careful.

const TYPES: {value: QuestionType; label: string}[] = [
    {value: "SINGLE_SELECT", label: "Single Select"},
    {value: "MULTI_SELECT", label: "Multi Select"},
    {value: "TRUE_FALSE", label: "True / False"},
];

const MAX_CORRECT = 3;

function blankQuestion(kind: QuestionType = "SINGLE_SELECT"): Question {
    if (kind === "TRUE_FALSE") {
        return {
            QuestionType: kind, Text: "", CorrectSelections: 1,
            Choices: [{Text: "True", IsCorrect: true, Rationale: ""},
                      {Text: "False", IsCorrect: false, Rationale: ""}],
        };
    }
    return {
        QuestionType: kind, Text: "", CorrectSelections: 1,
        Choices: [0, 1, 2, 3].map(() => ({Text: "", IsCorrect: false, Rationale: ""})),
    };
}

export default function QuizBuilder() {
    const {usid} = useParams();
    const navigate = useNavigate();
    const sessionContext = useSessionContext();

    const [questions, setQuestions] = useState<Question[]>([]);
    const [selected, setSelected] = useState(0);
    const [passingScore, setPassingScore] = useState(80);
    const [shuffle, setShuffle] = useState(false);
    const [partialCredit, setPartialCredit] = useState(false);
    const [editable, setEditable] = useState(true);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState<string | null>(null);
    const [showingSettings, setShowingSettings] = useState(false);
    const [proposals, setProposals] = useState<Proposal[] | null>(null);
    const [proposing, setProposing] = useState(false);
    const [skipped, setSkipped] = useState<string[]>([]);

    const load = useCallback(() => {
        if (!usid) return;
        setLoading(true);
        getQuiz(sessionContext, usid)
            .then(result => {
                setEditable(result.Editable);
                if (result.Quiz) {
                    setQuestions(result.Quiz.Questions);
                    setPassingScore(result.Quiz.PassingScore);
                    setShuffle(result.Quiz.ShuffleAnswers);
                    setPartialCredit(result.Quiz.AllowPartialCredit);
                } else {
                    // A NEW QUIZ OPENS ON ONE EMPTY QUESTION, which is what the builder is for. An
                    // empty rail would leave the page looking broken until somebody found Add Question.
                    setQuestions([blankQuestion()]);
                }
                setError(null);
            })
            .catch(problem => setError(problem?.response?.data?.Cause ?? "Could not read the quiz."))
            .finally(() => setLoading(false));
    }, [sessionContext, usid]);

    useEffect(load, [load]);

    const current = questions[selected];

    const update = (index: number, change: Partial<Question>) => {
        setSaved(null);
        setQuestions(previous => previous.map((one, i) => i === index ? {...one, ...change} : one));
    };

    const changeType = (index: number, kind: QuestionType) => {
        const question = questions[index];
        if (kind === "TRUE_FALSE") {
            // TRUE/FALSE REPLACES THE CHOICES rather than keeping whatever was typed: its two are
            // fixed, and carrying four half-written options into it would produce a question that
            // cannot be saved and does not say why.
            update(index, {...blankQuestion("TRUE_FALSE"), Text: question.Text});
            return;
        }
        const selections = kind === "SINGLE_SELECT" ? 1 : question.CorrectSelections;
        let choices = question.Choices;
        if (question.QuestionType === "TRUE_FALSE") choices = blankQuestion(kind).Choices;
        if (kind === "SINGLE_SELECT") {
            // Only one may stay marked, and it is the first -- silently keeping two would be a quiz
            // that refuses to save for a reason nothing on screen shows.
            let seen = false;
            choices = choices.map(choice => {
                const keep = choice.IsCorrect && !seen;
                if (keep) seen = true;
                return {...choice, IsCorrect: keep};
            });
        }
        update(index, {QuestionType: kind, CorrectSelections: selections, Choices: choices});
    };

    const setChoice = (index: number, choiceIndex: number, change: Partial<Question["Choices"][0]>) => {
        const question = questions[index];
        update(index, {
            Choices: question.Choices.map((choice, i) => i === choiceIndex ? {...choice, ...change} : choice),
        });
    };

    const toggleCorrect = (index: number, choiceIndex: number) => {
        const question = questions[index];
        if (question.QuestionType === "MULTI_SELECT") {
            setChoice(index, choiceIndex, {IsCorrect: !question.Choices[choiceIndex].IsCorrect});
            return;
        }
        // Single select and True/False: exactly one, so marking one unmarks the rest.
        update(index, {
            Choices: question.Choices.map((choice, i) => ({...choice, IsCorrect: i === choiceIndex})),
        });
    };

    const addQuestion = () => {
        setQuestions(previous => [...previous, blankQuestion()]);
        setSelected(questions.length);
        setSaved(null);
    };

    const duplicateQuestion = (index: number) => {
        const copy: Question = JSON.parse(JSON.stringify(questions[index]));
        delete copy.USID;
        copy.Choices = copy.Choices.map(choice => { const {USID, ...rest} = choice as any; return rest; });
        setQuestions(previous => [...previous.slice(0, index + 1), copy, ...previous.slice(index + 1)]);
        setSelected(index + 1);
        setSaved(null);
    };

    const removeQuestion = (index: number) => {
        setQuestions(previous => previous.filter((_, i) => i !== index));
        setSelected(previous => Math.max(0, previous > index ? previous - 1 : Math.min(previous, questions.length - 2)));
        setSaved(null);
    };

    const markedCorrect = useMemo(
        () => (current?.Choices ?? []).filter(choice => choice.IsCorrect).length, [current]);

    const propose = async () => {
        if (!usid) return;
        setProposing(true);
        setError(null);
        try {
            const result = await proposeQuestions(sessionContext, usid, 5);
            setProposals(result.Questions);
            setSkipped(result.SkippedAttachments ?? []);
        } catch (problem: any) {
            setError(problem?.response?.data?.Cause ?? "Could not propose questions.");
        } finally {
            setProposing(false);
        }
    };

    const accept = (index: number) => {
        const proposal = proposals?.[index];
        if (!proposal) return;
        setQuestions(previous => [...previous, proposal]);
        setProposals(previous => (previous ?? []).filter((_one, i) => i !== index));
        setSelected(questions.length);
        setSaved(null);
    };

    const save = async (changes?: {PassingScore?: number; ShuffleAnswers?: boolean; AllowPartialCredit?: boolean}) => {
        if (!usid) return;
        setSaving(true);
        setError(null);
        try {
            await saveQuiz(sessionContext, usid, {Questions: questions, ...changes});
            setSaved("Saved.");
            load();
        } catch (problem: any) {
            setError(problem?.response?.data?.Cause ?? "Could not save the quiz.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div style={{padding: 40}}><CircularProgress size={22}/></div>;
    }

    return (
        <div style={{display: "flex", flexDirection: "column", height: "100%", backgroundColor: "#F7F8FA"}}>
            {/* The bar: back on the left, the two actions on the right, as in the design. */}
            <div style={{display: "flex", alignItems: "center", justifyContent: "space-between",
                         padding: "12px 20px", backgroundColor: White,
                         borderBottom: `1px solid ${Grey2}`, gap: 16}}>
                <div style={{display: "flex", alignItems: "center", gap: 16, minWidth: 0}}>
                    <Button size="small" startIcon={<ArrowBackOutlined/>}
                            onClick={() => navigate(`/object/${usid}`)}
                            sx={{textTransform: "none", borderRadius: "999px", border: `1px solid ${Grey2}`,
                                 paddingInline: 2}}>
                        Back To Document
                    </Button>
                    <Typography style={{fontSize: 18, fontWeight: 600, color: Grey17, whiteSpace: "nowrap"}}>
                        Quiz Builder
                    </Typography>
                    {!editable ? (
                        <Chip size="small" label="Frozen — this revision is released"
                              sx={{fontSize: 11, height: 22}}/>
                    ) : null}
                </div>
                <div style={{display: "flex", alignItems: "center", gap: 10}}>
                    {saved ? <Typography style={{fontSize: 12, color: Grey5}}>{saved}</Typography> : null}
                    <Button size="small" variant="outlined" startIcon={<SettingsOutlined/>}
                            onClick={() => setShowingSettings(true)}
                            sx={{textTransform: "none", borderRadius: "999px", paddingInline: 2}}>
                        Quiz Settings
                    </Button>
                    <Button size="small" variant="outlined" startIcon={<AutoAwesomeOutlined/>}
                            disabled={!editable || proposing} onClick={propose}
                            sx={{textTransform: "none", borderRadius: "999px", paddingInline: 2}}>
                        {proposing ? "Reading the record..." : "Assist With AI"}
                    </Button>
                    <Button size="small" variant="contained" disabled={!editable || saving}
                            onClick={() => save()}
                            sx={{textTransform: "none", borderRadius: "999px", paddingInline: 3}}>
                        Save Quiz
                    </Button>
                </div>
            </div>

            <div style={{display: "flex", flex: 1, minHeight: 0}}>
                {/* The rail of questions. */}
                <div style={{width: 300, borderRight: `1px solid ${Grey2}`, backgroundColor: White,
                             overflowY: "auto", padding: "18px 14px"}}>
                    <Typography style={{fontSize: 15, fontWeight: 600, color: Grey17}}>Quiz Questions</Typography>
                    <Typography style={{fontSize: 12, color: Grey5, marginBottom: 12}}>
                        {questions.length} {questions.length === 1 ? "question" : "questions"}
                    </Typography>

                    {questions.map((question, index) => (
                        <div key={index} onClick={() => setSelected(index)}
                             style={{display: "flex", alignItems: "flex-start", gap: 10, padding: 10,
                                     borderRadius: 8, cursor: "pointer", marginBottom: 6,
                                     backgroundColor: index === selected ? "#EEF4FF" : "transparent"}}>
                            <div style={{width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                                         backgroundColor: Blue3, color: White, fontSize: 12,
                                         display: "flex", alignItems: "center", justifyContent: "center"}}>
                                {index + 1}
                            </div>
                            <div style={{flex: 1, minWidth: 0}}>
                                <Typography style={{fontSize: 13, fontWeight: 600, color: Grey17}}>
                                    {TYPES.find(t => t.value === question.QuestionType)?.label}
                                </Typography>
                                <Typography style={{fontSize: 12, color: Grey5, overflow: "hidden",
                                                    textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                                    {question.Text || "Add question text..."}
                                </Typography>
                            </div>
                            {editable ? (
                                <>
                                    <Tooltip title="Duplicate">
                                        <IconButton size="small"
                                                    onClick={event => { event.stopPropagation(); duplicateQuestion(index); }}>
                                            <ContentCopyOutlined sx={{fontSize: 15}}/>
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={questions.length === 1
                                        ? "A quiz needs at least one question" : "Delete"}>
                                        <span>
                                            <IconButton size="small" disabled={questions.length === 1}
                                                        onClick={event => { event.stopPropagation(); removeQuestion(index); }}>
                                                <DeleteOutlineOutlined sx={{fontSize: 15, color: "#B42318"}}/>
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </>
                            ) : null}
                        </div>
                    ))}

                    {editable ? (
                        <Button fullWidth onClick={addQuestion}
                                sx={{textTransform: "none", marginTop: 1, borderRadius: 2,
                                     border: `1px dashed ${Blue3}`, color: Blue3}}>
                            + Add Question
                        </Button>
                    ) : null}
                </div>

                {/* The editor for the selected question. */}
                <div style={{flex: 1, overflowY: "auto", padding: "24px 32px", minWidth: 0}}>
                    {current ? (
                        <div style={{maxWidth: 940, margin: "0 auto", backgroundColor: White,
                                     border: `1px solid ${Grey2}`, borderRadius: 12, padding: 24}}>
                            <div style={{display: "flex", alignItems: "center",
                                         justifyContent: "space-between", gap: 16, marginBottom: 20}}>
                                <div style={{display: "flex", alignItems: "center", gap: 10}}>
                                    <div style={{width: 24, height: 24, borderRadius: "50%",
                                                 backgroundColor: Blue3, color: White, fontSize: 12,
                                                 display: "flex", alignItems: "center",
                                                 justifyContent: "center"}}>{selected + 1}</div>
                                    <Typography style={{fontSize: 16, fontWeight: 600, color: Grey17}}>
                                        Question Editor
                                    </Typography>
                                </div>
                                <ToggleButtonGroup size="small" exclusive value={current.QuestionType}
                                                   disabled={!editable}
                                                   onChange={(_event, value) => value && changeType(selected, value)}>
                                    {TYPES.map(type => (
                                        <ToggleButton key={type.value} value={type.value}
                                                      sx={{textTransform: "none", fontSize: 12, paddingInline: 2}}>
                                            {type.label}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            </div>

                            <Typography style={{fontSize: 13, fontWeight: 600, color: Grey17}}>
                                Question Text <span style={{color: "#B42318"}}>*</span>
                            </Typography>
                            <TextField fullWidth multiline minRows={3} disabled={!editable}
                                       placeholder="Add question text..."
                                       value={current.Text}
                                       onChange={event => update(selected, {Text: event.target.value})}
                                       sx={{marginTop: 1, marginBottom: 3}}/>

                            <div style={{display: "flex", alignItems: "center", justifyContent: "space-between",
                                         gap: 16, marginBottom: 10}}>
                                <Typography style={{fontSize: 13, fontWeight: 600, color: Grey17}}>
                                    Answer Choices <span style={{color: Grey5, fontWeight: 400}}>(select correct)</span>
                                </Typography>

                                {/* HOW MANY SELECTIONS MAKE A COMPLETE ANSWER. Offered only for multi
                                    select, because the other two are always one -- and the taker is
                                    SHOWN this number, so it is the author's decision rather than a
                                    count of whatever happens to be ticked. */}
                                {current.QuestionType === "MULTI_SELECT" ? (
                                    <div style={{display: "flex", alignItems: "center", gap: 8}}>
                                        <Typography style={{fontSize: 12, color: Grey6}}>
                                            A complete answer is
                                        </Typography>
                                        <ToggleButtonGroup size="small" exclusive disabled={!editable}
                                                           value={current.CorrectSelections}
                                                           onChange={(_event, value) => value &&
                                                               update(selected, {CorrectSelections: value})}>
                                            {[1, 2, 3].slice(0, MAX_CORRECT).map(n => (
                                                <ToggleButton key={n} value={n}
                                                              sx={{textTransform: "none", fontSize: 12,
                                                                   paddingInline: 1.5}}>{n}</ToggleButton>
                                            ))}
                                        </ToggleButtonGroup>
                                        <Typography style={{fontSize: 12, color: Grey6}}>
                                            selection{current.CorrectSelections === 1 ? "" : "s"}
                                        </Typography>
                                    </div>
                                ) : null}
                            </div>

                            {/* SAID BEFORE SAVING, NOT AFTER. The server refuses a question whose count
                                disagrees with what is ticked; being told here, while looking at the
                                ticks, is the difference between a hint and an error message. */}
                            {markedCorrect !== current.CorrectSelections ? (
                                <Typography style={{fontSize: 12, color: "#B54708", marginBottom: 10}}>
                                    The taker will be told to select {current.CorrectSelections}
                                    {" "}but {markedCorrect} {markedCorrect === 1 ? "is" : "are"} marked correct.
                                </Typography>
                            ) : null}

                            {current.Choices.map((choice, choiceIndex) => (
                                <div key={choiceIndex}
                                     style={{border: `1px solid ${Grey2}`, borderRadius: 10, padding: 12,
                                             marginBottom: 10}}>
                                    <div style={{display: "flex", alignItems: "center", gap: 10}}>
                                        {current.QuestionType === "MULTI_SELECT" ? (
                                            <Checkbox size="small" disabled={!editable}
                                                      checked={choice.IsCorrect}
                                                      onChange={() => toggleCorrect(selected, choiceIndex)}/>
                                        ) : (
                                            <Radio size="small" disabled={!editable}
                                                   checked={choice.IsCorrect}
                                                   onChange={() => toggleCorrect(selected, choiceIndex)}/>
                                        )}
                                        <TextField fullWidth size="small" placeholder="Add answer..."
                                                   disabled={!editable || current.QuestionType === "TRUE_FALSE"}
                                                   value={choice.Text}
                                                   onChange={event => setChoice(selected, choiceIndex,
                                                                                {Text: event.target.value})}/>
                                        {editable && current.QuestionType !== "TRUE_FALSE" ? (
                                            <Tooltip title={current.Choices.length <= 2
                                                ? "A question needs at least two choices" : "Remove"}>
                                                <span>
                                                    <IconButton size="small"
                                                                disabled={current.Choices.length <= 2}
                                                                onClick={() => update(selected, {
                                                                    Choices: current.Choices.filter((_, i) => i !== choiceIndex),
                                                                })}>
                                                        <DeleteOutlineOutlined sx={{fontSize: 17, color: "#B42318"}}/>
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        ) : null}
                                    </div>
                                    <div style={{display: "flex", alignItems: "center", gap: 10, marginTop: 8}}>
                                        <LightbulbOutlined sx={{fontSize: 17, color: Grey5, marginLeft: "9px"}}/>
                                        <TextField fullWidth size="small" disabled={!editable}
                                                   placeholder="Add rationale..."
                                                   value={choice.Rationale ?? ""}
                                                   onChange={event => setChoice(selected, choiceIndex,
                                                                                {Rationale: event.target.value})}/>
                                    </div>
                                </div>
                            ))}

                            {editable && current.QuestionType !== "TRUE_FALSE" ? (
                                <Button fullWidth onClick={() => update(selected, {
                                    Choices: [...current.Choices, {Text: "", IsCorrect: false, Rationale: ""}],
                                })}
                                        sx={{textTransform: "none", borderRadius: 2,
                                             border: `1px dashed ${Blue3}`, color: Blue3}}>
                                    + Add Option
                                </Button>
                            ) : null}

                            <div style={{display: "flex", alignItems: "center", justifyContent: "space-between",
                                         marginTop: 24}}>
                                <Button size="small" startIcon={<ArrowBack/>} disabled={selected === 0}
                                        onClick={() => setSelected(selected - 1)}
                                        sx={{textTransform: "none"}}>Previous</Button>
                                <Typography style={{fontSize: 13, color: Grey6}}>
                                    Question {selected + 1} of {questions.length}
                                </Typography>
                                <Button size="small" endIcon={<ArrowForward/>}
                                        disabled={selected >= questions.length - 1}
                                        onClick={() => setSelected(selected + 1)}
                                        sx={{textTransform: "none"}}>Next</Button>
                            </div>
                        </div>
                    ) : null}

                    {error ? (
                        <Typography style={{fontSize: 13, color: "#B42318", marginTop: 16,
                                            maxWidth: 940, margin: "16px auto 0"}}>{error}</Typography>
                    ) : null}
                </div>
            </div>

            {/* ── the proposals ──────────────────────────────────────────────────────────────
                REVIEWED ONE AT A TIME, with the rationale visible, because accepting a question means
                standing behind it. A "take all" button would make the review a formality, which is the
                one thing this dialog exists to prevent. */}
            <Dialog open={proposals !== null} onClose={() => setProposals(null)} maxWidth="md" fullWidth>
                <DialogTitle style={{fontSize: 20, fontWeight: 700, color: Grey17}}>
                    Proposed questions
                </DialogTitle>
                <DialogContent>
                    <Typography style={{fontSize: 13, color: Grey5, marginBottom: 4}}>
                        Drawn from this record's description, its form answers and any plain-text
                        attachments. Nothing is saved until you accept it and save the quiz.
                    </Typography>
                    {skipped.length ? (
                        <Typography style={{fontSize: 12, color: "#B54708", marginBottom: 12}}>
                            Not read: {skipped.join(", ")}. Only plain-text attachments are read for now.
                        </Typography>
                    ) : null}

                    {proposals?.length === 0 ? (
                        <Typography style={{fontSize: 14, color: Grey17, marginTop: 12}}>
                            Nothing proposed. There may not be enough in this record to ask a fair question
                            about yet — returning none is a better answer than padding.
                        </Typography>
                    ) : null}

                    {(proposals ?? []).map((proposal, index) => (
                        <div key={index} style={{border: `1px solid ${Grey2}`, borderRadius: 10,
                                                 padding: 16, marginBottom: 12}}>
                            <div style={{display: "flex", justifyContent: "space-between",
                                         alignItems: "flex-start", gap: 12}}>
                                <Typography style={{fontSize: 15, color: Grey17, flex: 1}}>
                                    {proposal.Text}
                                </Typography>
                                <Chip size="small" label={TYPES.find(t => t.value === proposal.QuestionType)?.label}
                                      sx={{fontSize: 11, height: 22}}/>
                            </div>
                            {proposal.QuestionType === "MULTI_SELECT" ? (
                                <Typography style={{fontSize: 12, color: Grey6, marginTop: 2}}>
                                    Select {proposal.CorrectSelections} of these.
                                </Typography>
                            ) : null}
                            {proposal.Choices.map((choice, choiceIndex) => (
                                <div key={choiceIndex} style={{marginTop: 8}}>
                                    <Typography style={{fontSize: 14,
                                                        color: choice.IsCorrect ? "#067647" : Grey17}}>
                                        {choice.IsCorrect ? "✓ " : "· "}{choice.Text}
                                    </Typography>
                                    {choice.Rationale ? (
                                        <Typography style={{fontSize: 12, color: Grey6, marginLeft: 16}}>
                                            {choice.Rationale}
                                        </Typography>
                                    ) : null}
                                </div>
                            ))}
                            <div style={{display: "flex", gap: 8, marginTop: 14}}>
                                <Button size="small" variant="contained" onClick={() => accept(index)}
                                        sx={{textTransform: "none"}}>
                                    Accept and edit
                                </Button>
                                <Button size="small" color="inherit"
                                        onClick={() => setProposals(previous =>
                                            (previous ?? []).filter((_one, i) => i !== index))}
                                        sx={{textTransform: "none", color: Grey6}}>
                                    Discard
                                </Button>
                            </div>
                        </div>
                    ))}
                </DialogContent>
                <DialogActions style={{padding: "12px 24px 20px"}}>
                    <Button onClick={() => setProposals(null)} sx={{textTransform: "none"}}>Close</Button>
                    <Button variant="outlined" disabled={proposing} onClick={propose}
                            sx={{textTransform: "none"}}>Propose more</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={showingSettings} onClose={() => setShowingSettings(false)} maxWidth="sm" fullWidth>
                <DialogTitle style={{fontSize: 22, fontWeight: 700, color: Grey17}}>Quiz Settings</DialogTitle>
                <DialogContent>
                    <div style={{display: "flex", alignItems: "flex-start", justifyContent: "space-between",
                                 gap: 24, padding: "18px 0", borderBottom: `1px solid ${Grey2}`}}>
                        <div>
                            <Typography style={{fontSize: 15, fontWeight: 600, color: Grey17}}>Passing Score</Typography>
                            <Typography style={{fontSize: 13, color: Grey5, marginTop: 4}}>
                                The minimum percentage needed to pass.
                            </Typography>
                        </div>
                        <TextField size="small" type="number" disabled={!editable} value={passingScore}
                                   onChange={event => setPassingScore(Number(event.target.value))}
                                   slotProps={{
                                       htmlInput: {min: 1, max: 100, style: {width: 52}},
                                       // slotProps.input, not InputProps: MUI v9 removed the latter.
                                       input: {endAdornment: <span style={{color: Grey5}}>%</span>},
                                   }}/>
                    </div>
                    <div style={{display: "flex", alignItems: "flex-start", justifyContent: "space-between",
                                 gap: 24, padding: "18px 0", borderBottom: `1px solid ${Grey2}`}}>
                        <div>
                            <Typography style={{fontSize: 15, fontWeight: 600, color: Grey17}}>Shuffle Answers</Typography>
                            <Typography style={{fontSize: 13, color: Grey5, marginTop: 4}}>
                                Randomize the order of answer choices for each user, and quiz retakes.
                            </Typography>
                        </div>
                        <Switch checked={shuffle} disabled={!editable}
                                onChange={event => setShuffle(event.target.checked)}/>
                    </div>
                    <div style={{display: "flex", alignItems: "flex-start", justifyContent: "space-between",
                                 gap: 24, padding: "18px 0"}}>
                        <div>
                            <Typography style={{fontSize: 15, fontWeight: 600, color: Grey17}}>Partial Credit</Typography>
                            <Typography style={{fontSize: 13, color: Grey5, marginTop: 4}}>
                                A partly-right multi-select answer earns part of the mark, instead of the
                                question being all or nothing.
                            </Typography>
                        </div>
                        <Switch checked={partialCredit} disabled={!editable}
                                onChange={event => setPartialCredit(event.target.checked)}/>
                    </div>
                </DialogContent>
                <DialogActions style={{padding: "12px 24px 20px"}}>
                    <Button onClick={() => setShowingSettings(false)} sx={{textTransform: "none"}}>Cancel</Button>
                    <Button variant="contained" disabled={!editable || saving}
                            onClick={async () => {
                                await save({PassingScore: passingScore, ShuffleAnswers: shuffle,
                                            AllowPartialCredit: partialCredit});
                                setShowingSettings(false);
                            }}
                            sx={{textTransform: "none", borderRadius: "999px", paddingInline: 3}}>
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
